'use client';

import Script from 'next/script';
import { AdvantagesCarousel } from 'src/components/Carousel/Carousels';
import { PageBlocks } from 'src/components/PageBlocks';
import { type Anchor } from 'src/components/ProductDetailed/AnchorMenu';
import { FreeTierBanner } from 'src/components/ProductDetailed/FreeTierBanner';
import { consoleUrl } from 'src/global';
import { useFormatConsoleUrl } from 'src/hooks/useFormatConsoleUrl';
import { useIntersectionObserveForms } from 'src/hooks/useIntersectionObserverForms';
import { Layout } from 'src/layout';
import { type ProductProps } from 'src/types/backendContent';
import { AccordionProps } from 'src/uikit/Accordion/type';
import { Button } from 'src/uikit/Button';
import { PersonalManager } from 'src/uikit/PersonalManager';
import { type ButtonProps, ProductHero } from 'src/uikit/ProductHero';
import { scrollToElementWithId } from 'src/utils/scrollToElementWithId';

import { PlatformsSlugs } from './constants';
import { RegistryFormBlock } from './helperComponents';
import {
  BUTTON_TITLE,
  SECOND_BUTTON_TITLE,
  SELECT_OPTIONS_GPU,
  SELECT_OPTIONS_VDI,
} from './mock';
import s from './Product.module.scss';
import { ButtonMockProps } from './types';

export function ProductPage({
  data,
  anchors,
}: {
  data: ProductProps;
  anchors: Anchor[];
}) {
  const { formRef, setAnchorId } = useIntersectionObserveForms();

  const productAdvancedUrl = useFormatConsoleUrl(
    'https://advanced.cloud.ru',
    'body',
    BUTTON_TITLE,
    'https://advanced.cloud.ru/',
  );
  const productEvolutionUrl = useFormatConsoleUrl(
    consoleUrl,
    'body',
    BUTTON_TITLE,
  );

  const productPlatform =
    data.productPlatforms?.length !== 1
      ? 'Кроссплатформенные сервисы'
      : data.productPlatforms?.[0].title;

  const scrollToForm = () => {
    scrollToElementWithId('form');
    setAnchorId('products_anchor_to_form');
  };

  const isEvolutionPage =
    data.productPlatforms[0].slug === PlatformsSlugs.Evolution;

  const DefaultButton: ButtonMockProps = {
    title: SECOND_BUTTON_TITLE,
  };

  const AdvancedButton: ButtonMockProps = {
    title: BUTTON_TITLE,
    href: productAdvancedUrl,
  };

  const EvolutionButton: ButtonMockProps = {
    title: BUTTON_TITLE,
    href: productEvolutionUrl,
  };

  //TODO: отказаться когда заедет в CMS
  const EvolutionBareMetalButton: ButtonMockProps = {
    title: 'Арендовать сервер',
    href: useFormatConsoleUrl(consoleUrl, 'body', 'Арендовать сервер'),
  };

  const getHeroButtons = (buttonTitle?: string) => {
    let buttonsMock: ButtonMockProps[] = [];

    // Стандартные для платформ кнопки
    switch (data.productPlatforms[0].slug) {
      case PlatformsSlugs.Advanced:
        buttonsMock = [AdvancedButton];
        break;
      case PlatformsSlugs.Evolution:
        buttonsMock = [EvolutionButton];
        break;
      default:
        buttonsMock = [DefaultButton];
        break;
    }

    // Кроссплатформенный продукт
    if (data.productPlatforms.length > 1) {
      buttonsMock = [DefaultButton];
    }

    // TODO: убрать код, когда удалим private-cloud
    // Нестандартные для платформы кнопки
    if (data.slug === 'private-cloud') {
      buttonsMock = [DefaultButton];
    }

    if (data.slug === 'evolution-bare-metal') {
      buttonsMock = [EvolutionBareMetalButton];
    }

    // Добавление кнопки с документацией
    if (data.docsLink) {
      const DocumentationButton: ButtonMockProps = {
        title: 'Документация сервиса',
        href: data.docsLink,
      };
      buttonsMock.push(DocumentationButton);
    }

    return buttonsMock.map((button, index) => {
      return {
        title: index === 0 && buttonTitle ? buttonTitle : button.title,
        href: button.href,
        target: Button.targets.Blank,
        onClick: button.href ? undefined : scrollToForm,
        analyticsOptions: {
          action: button.href ? button.href : 'click',
          clickZone: 'body',
          clickElement: 'button',
          clickContent: button.title,
          uniqueId: `product-hero-button-${button.title}`,
          transitionType: button.href ? 'outside-link' : 'inside-link',
        },
      } satisfies ButtonProps;
    });
  };

  const includeFreeTierSlugs = ['virtual-workspaces'];
  const isFreeTierBanner = includeFreeTierSlugs.includes(data.slug);

  let multipleComment = undefined;
  switch (data.slug) {
    case 'virtual-workspaces':
      multipleComment = SELECT_OPTIONS_VDI;
      break;
    case 'vychislitelnyye-moschnosti-s-gpu':
      multipleComment = SELECT_OPTIONS_GPU;
      break;
    default:
      multipleComment = undefined;
      break;
  }

  const faqBlock = data.content.blocks.find(({ type }) => type === 'Accordion');

  let jsonFaq = {};

  if (faqBlock) {
    const { cards } = faqBlock.data as Pick<AccordionProps, 'cards'>;

    const entitys = cards.map((entity) => {
      return {
        '@type': 'Question',
        name: entity.title,
        acceptedAnswer: {
          '@type': 'Answer',
          text: entity.richText,
        },
      };
    });

    jsonFaq = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [...entitys],
    };
  }

  return (
    <Layout withoutHeaderShadow>
      <div className={s.root}>
        <ProductHero
          breadcrumbs={data.content.breadcrumbs}
          platforms={data.productPlatforms}
          service={{
            icon: data.icon,
            title: data.title,
          }}
          description={data.description}
          image={data.icon3d}
          anchorMenu={{ anchors: anchors, slug: data.slug }}
          isLegal={data.isLegal}
          available={data.available}
          buttons={getHeroButtons(data.buttonTitle)}
          tags={Array.isArray(data.tags) ? data.tags : undefined}
        />

        {isFreeTierBanner && <FreeTierBanner />}

        <PageBlocks
          blocks={data.content.blocks}
          onConsultationClick={() => setAnchorId('products_anchor_to_form')}
        />

        {isEvolutionPage && (
          <>
            <AdvantagesCarousel />

            <PersonalManager
              button={{
                title: 'Получить консультацию',
                onClick: scrollToForm,
                analyticsOptions: {
                  action: 'click',
                  clickZone: 'body',
                  clickElement: 'button',
                  clickContent: 'Получить консультацию',
                  uniqueId: `${data.slug}-personal-manager-button-scroll-to-form`,
                  transitionType: 'inside-link',
                },
              }}
            />
          </>
        )}

        {data.available && data.form && (
          <RegistryFormBlock
            {...data.form}
            formRef={formRef}
            slug={data.slug}
            marketingEventID={data.marketingEventID}
            productPlatform={productPlatform}
            multipleComment={multipleComment}
          />
        )}
      </div>
      {faqBlock && (
        <Script
          id="microfaq"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonFaq) }}
        />
      )}
    </Layout>
  );
}
